import axios, {registerTokenRefresher} from './axiosInstance';
import {getTokenId, isScopeMatched, SCOPE_USER} from './tokenClaims';

/**
 * [AT 수명 24시간에 맞춘 갱신주기]
 *  예전에는 AT 가 1시간이라 '50분 경과 시 갱신 + 5분마다 점검' 이었다. 그래서 활성
 *  사용자는 약 55분마다 재발급을 했고, 그 빈도 때문에 기기를 번갈아 쓸 때 재발급이
 *  자주 충돌했다(서버가 계정당 RT 를 1개만 보관하고 매번 교체했기 때문).
 *  서버에서 RT 를 기기별로 보관하도록 고치고 AT 를 24시간으로 늘렸으므로,
 *  갱신도 그에 맞춰 늦춘다. (20시간 + 30분 점검 < 24시간)
 */

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  사용자 토큰 관리
 * ════════════════════════════════════════════════════════════════════════════════
 *  [저장소가 둘로 갈린다 — 설계 의도이므로 그대로 둔다]
 *   로그인 화면의 '로그인 유지' 선택에 따라 저장위치가 달라진다.
 *     유지함   : localStorage    (브라우저를 닫아도 남는다)
 *     유지안함 : sessionStorage  (탭을 닫으면 사라진다)
 *   키 이름은 양쪽 모두 AToken / lastLoginTime 이다.
 *   그래서 '토큰이 어느 저장소에 있는지' 를 먼저 찾고, 갱신도 그 저장소에 한다.
 *   (관리자는 localStorage 만 쓰므로 services/adminAuth.js 로 따로 두었다)
 *
 *  [무엇을 고쳤나]
 *   1) 갱신시점이 만료 이후였다
 *      AT 는 1시간인데 '1시간마다 점검 + 1시간 경과 시 갱신' 이라
 *      최악의 경우 2시간 가까이 만료된 토큰으로 지냈다.
 *      (주석에는 '30분 경과 시 재발급' 이라 적혀 있었는데 코드는 1시간이었다)
 *      -> 5분마다 점검하고 50분이 지나면 미리 갱신한다.
 *
 *   2) 응답 검증이 없었다
 *      Authorization 응답헤더가 비어도 그대로 저장해서
 *      localStorage 에 "null" 이라는 문자열이 들어갔다.
 *      그 값은 truthy 라 라우트 가드를 통과해버려
 *      '토큰은 있는데 모든 API 가 401' 인 상태가 계속되었다.
 *
 *   3) 시각 저장형식이 달랐다
 *      로그인은 Date 객체를 그대로 넣어 브라우저 기본 문자열이 저장되었다.
 *      환경에 따라 해석이 달라질 수 있어 ISO 로 통일한다. (기존 형식도 계속 읽는다)
 *
 *  [건드리지 않은 것]
 *   저장소 분기와 30일 경과 자동로그아웃은 기존 로직을 그대로 유지한다.
 *   소셜로그인(네이버/구글/카카오) 등 로그인 저장지점도 손대지 않았다.
 * ════════════════════════════════════════════════════════════════════════════════
 */

export const USER_AT_KEY = 'AToken';
export const USER_LOGIN_TIME_KEY = 'lastLoginTime';

/** AT 는 24시간이므로 20시간이 지나면 미리 갱신한다 */
export const USER_REISSUE_AFTER_MS = 20 * 60 * 60 * 1000;
/** 점검 주기 — 짧게 돌아도 실제 갱신은 위 조건을 넘길 때만 한다 */
export const USER_CHECK_INTERVAL_MS = 30 * 60 * 1000;
/** 로그인 후 30일이 지나면 자동 로그아웃 (기존 로직 유지) */
export const USER_MAX_SESSION_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * 토큰이 들어있는 저장소를 찾는다.
 *  sessionStorage 를 먼저 본다. 두 곳에 모두 있다면 이번 탭에서 '유지안함' 으로
 *  로그인한 것이므로 그쪽이 현재 세션이다.
 *  @returns {Storage|null}
 */
export const getUserStorage = () => {
    try {
        if (window.sessionStorage.getItem(USER_AT_KEY)) return window.sessionStorage;
        if (window.localStorage.getItem(USER_AT_KEY)) return window.localStorage;
    } catch (e) { /* 저장소 접근 실패는 무시 */ }
    return null;
};

export const getUserToken = () => {
    const store = getUserStorage();
    return store ? store.getItem(USER_AT_KEY) : null;
};

/**
 * 마지막으로 토큰을 받은 시각
 *  로그인 지점들이 Date 객체를 그대로 넣어온 경우(브라우저 기본 문자열)도 받아들인다.
 *  해석할 수 없으면 null 로 본다.
 */
export const getUserLoginTime = () => {
    const store = getUserStorage();
    if (!store) return null;
    try {
        const raw = store.getItem(USER_LOGIN_TIME_KEY);
        if (!raw) return null;
        const time = new Date(raw).getTime();
        return Number.isFinite(time) ? time : null;
    } catch (e) {
        return null;
    }
};

/** 사용자 세션 정리 — 토큰이 있던 저장소만 비운다 */
export const clearUserSession = () => {
    const store = getUserStorage();
    if (!store) return;
    try {
        store.removeItem(USER_AT_KEY);
        store.removeItem(USER_LOGIN_TIME_KEY);
    } catch (e) { /* 무시 */ }
};

/** 같은 순간에 재발급 요청이 여러 번 나가지 않도록 하나로 묶는다 (이 탭 안에서) */
let inFlight = null;

/*
 * ════════════════════════════════════════════════════════════════════════════════
 *  탭 사이의 재발급 중복을 막는 잠금
 * ════════════════════════════════════════════════════════════════════════════════
 *  [왜 필요한가]
 *   서버는 재발급 때마다 RT 를 교체한다. (MemberService.reissue)
 *     기존 RT 삭제 -> 새 RT 저장 -> 새 쿠키 발급
 *   그리고 '요청된 RT 가 Redis 의 RT 와 다르면' 탈취로 간주해
 *   Redis 의 RT 를 지워버린다.
 *
 *   그래서 여러 탭이 동시에 재발급하면 이런 일이 벌어진다.
 *     탭A 재발급 성공 -> RT 교체
 *     탭B 가 (교체 전에 보낸) 옛 쿠키로 도착 -> 불일치 -> 서버가 RT 삭제
 *     => 정상 로그인 상태였는데 세션이 죽고, 이후 모든 재발급이 401 이 된다
 *
 *   inFlight 는 같은 탭 안에서만 유효하므로 탭 사이는 막지 못한다.
 *   localStorage 로 짧은 잠금을 걸어 한 탭만 재발급하게 한다.
 * ════════════════════════════════════════════════════════════════════════════════
 */
const REISSUE_LOCK_KEY = 'userReissueLock';
const REISSUE_LOCK_TTL_MS = 15 * 1000;   // 요청이 끝나기 충분한 시간. 남아도 15초 뒤 자동해제

/** @returns {boolean} 내가 재발급을 맡았는지 */
const acquireReissueLock = () => {
    try {
        const raw = window.localStorage.getItem(REISSUE_LOCK_KEY);
        const now = Date.now();
        if (raw) {
            const at = Number(raw);
            // 아직 유효한 잠금이 있으면 다른 탭이 진행 중이다
            if (Number.isFinite(at) && at > 0 && now - at < REISSUE_LOCK_TTL_MS) return false;
        }
        window.localStorage.setItem(REISSUE_LOCK_KEY, String(now));
        return true;
    } catch (e) {
        return true;   // 저장소를 못 쓰면 잠금 없이 진행한다
    }
};

const releaseReissueLock = () => {
    try {
        window.localStorage.removeItem(REISSUE_LOCK_KEY);
    } catch (e) { /* 무시 */ }
};

/**
 * 서버가 '이 세션은 끝났다' 고 알려준 응답인지 판별한다.
 *  401 : Redis 에 RT 가 없거나 불일치 — 서버가 재발급을 거부했다
 *  403 : 권한 거부
 *  이때는 토큰을 남겨둘 이유가 없다. 남기면 라우트 가드는 통과하는데
 *  모든 API 가 401 인, 더 나쁜 상태가 된다.
 *
 *  [400 은 여기 넣지 않는다 — 운영 장애에서 배운 점]
 *   400 은 refresh-token 쿠키가 요청에 실리지 않았을 때 나온다(@CookieValue 필수).
 *   이것은 '세션이 끝났다' 가 아니라 '쿠키가 전달되지 않았다' 는 환경 문제이고,
 *   실제로 백엔드 쿠키 Path 설정이 어긋나 소셜 로그인 사용자에게서 발생했다.
 *   그 상황에서 세션을 지워 강제 로그아웃시키는 것은 과한 처분이므로,
 *   아래 reissueDisabled 로 '재시도만' 멈춘다. (무한 호출도 막고 로그아웃도 안 한다)
 */
const isSessionEnded = (status) => status === 401 || status === 403;

/*
 * 쿠키가 없어 재발급이 불가능한 환경이면, 이 페이지 세션 동안은 더 시도하지 않는다.
 * 새로고침하면 초기화된다.
 */
let reissueDisabled = false;

/**
 * 액세스토큰 재발급
 *  @returns {Promise<boolean>} 성공여부
 */
export const reissueUserToken = () => {
    if (inFlight) return inFlight;

    // 쿠키가 없어 재발급이 불가능한 환경이면 반복 호출하지 않는다 (로그아웃도 하지 않는다)
    if (reissueDisabled) return Promise.resolve(false);

    const store = getUserStorage();
    // 재발급 시점의 최신 토큰을 읽는다 (닫힌 변수에 갇힌 옛 토큰을 쓰지 않도록)
    const currentToken = store ? store.getItem(USER_AT_KEY) : null;
    if (!currentToken) return Promise.resolve(false);

    /*
     * 다른 탭이 재발급 중이면 나는 보내지 않는다.
     * 그 탭이 성공하면 저장소에 새 토큰이 들어오므로 다음 점검에서 그 값을 쓴다.
     * (여기서 같이 보내면 옛 쿠키로 도착해 서버가 RT 를 지워버린다)
     */
    if (!acquireReissueLock()) {
        console.log('[UserAuth] 다른 탭이 재발급 중이라 건너뜁니다.');
        return Promise.resolve(true);
    }

    inFlight = axios({
        method: 'post',
        url: '/member/reissue',
        headers: {Authorization: currentToken},
        withCredentials: true,   // httpOnly 쿠키(refresh-token)를 함께 보내야 한다
    })
        .then((response) => {
            const headers = response?.headers;
            const issued = headers
                ? (typeof headers.get === 'function'
                    ? headers.get('Authorization') || headers.get('authorization')
                    : headers.Authorization || headers.authorization)
                : null;

            if (!issued || issued === 'null' || issued === 'undefined') {
                console.log('[UserAuth] 재발급 응답에 토큰이 없습니다.');
                return false;
            }

            /*
             * [2차 방어] 사용자 자리에는 사용자 영역 토큰만 넣는다.
             *  관리자 쪽에서 실제 사고가 났던 것과 같은 구조라 대칭으로 막아둔다.
             *  (관리자 토큰이 사용자 자리에 들어가면 일반 화면이 관리자 권한으로
             *   동작하게 되므로 더 위험하다)
             */
            if (!isScopeMatched(issued, SCOPE_USER)) {
                console.log('[UserAuth] 사용자 영역 토큰이 아닙니다 — 세션을 정리합니다.');
                clearUserSession();
                return false;
            }
            const previousId = getTokenId(currentToken);
            const issuedId = getTokenId(issued);
            if (previousId && issuedId && previousId !== issuedId) {
                console.log('[UserAuth] 재발급된 토큰의 계정이 다릅니다 — 세션을 정리합니다.');
                clearUserSession();
                return false;
            }

            // 반드시 '토큰이 있던 그 저장소' 에 다시 넣는다
            store.setItem(USER_AT_KEY, issued);
            store.setItem(USER_LOGIN_TIME_KEY, new Date().toISOString());
            console.log('[UserAuth] 토큰 재발급 성공');
            return true;
        })
        .catch((error) => {
            const status = error?.response?.status;

            /*
             * [여기가 운영에서 401 이 끝없이 반복된 원인이었다]
             *  예전에는 '실패해도 토큰을 지우지 않는다' 로 두었다. 작성중 화면에서
             *  튕기지 않게 하려는 의도였지만, 되살릴 수 없는 실패(401)에도 죽은 토큰이
             *  남아 5분마다 /member/reissue 를 다시 때렸다. 서버는 이미 RT 를 지운
             *  상태라 영원히 401 이고, 콘솔과 서버로그가 계속 더럽혀졌다.
             *
             *  401 은 'Redis 에 RT 가 없거나 불일치' 일 때만 나오고, 불일치면 서버가
             *  RT 를 삭제한다. 즉 401 을 받은 순간 그 세션은 복구 불가다.
             *  그러면 토큰을 남겨둘 이유가 없다 — 남겨두면 라우트 가드는 통과하는데
             *  모든 API 가 401 인, 더 나쁜 상태가 된다. 그래서 정리하고 멈춘다.
             */
            if (isSessionEnded(status)) {
                console.log('[UserAuth] 세션이 만료되어 정리합니다. (' + status + ')');
                clearUserSession();
                return false;
            }

            /*
             * 400 — 쿠키가 전달되지 않았다. 세션은 건드리지 않고 재시도만 멈춘다.
             * 액세스토큰이 살아 있는 동안은 그대로 사용하고, 만료되면 실제 API 401 에서
             * 각 화면이 로그인으로 유도한다. (강제 로그아웃시키지 않는다)
             */
            if (status === 400) {
                reissueDisabled = true;
                console.log('[UserAuth] 재발급 쿠키가 없어 재시도를 중단합니다. (400)');
                return false;
            }

            // 네트워크 끊김 / 5xx / 타임아웃 — 일시적 장애다. 토큰을 지우지 않고 다음 점검에서 재시도한다
            console.log('[UserAuth] 토큰 재발급 일시실패 — 다음 점검에서 재시도', status || error?.message || '');
            return false;
        })
        .finally(() => {
            inFlight = null;
            releaseReissueLock();
        });

    return inFlight;
};

/** 30일 경과 시 서버 로그아웃까지 호출하고 저장소를 비운다 (기존 로직 유지) */
const logoutExpiredSession = async (token) => {
    try {
        await axios({
            method: 'post',
            url: '/member/logout',
            headers: {Authorization: token},
        });
    } catch (e) { /* 서버 호출 실패와 무관하게 로컬은 정리한다 */ }
    clearUserSession();
};

/**
 * 필요하면 재발급한다.
 *  @param force true 면 경과시간과 무관하게 즉시 재발급
 *  @returns {Promise<boolean>} 세션이 유효한 상태로 끝났는지
 */
export const ensureUserToken = async (force = false) => {
    const token = getUserToken();
    if (!token) return false;

    const loginTime = getUserLoginTime();

    /*
     * 시각 정보가 없으면(예전 로그인 / 저장 실패) 판단할 수 없다.
     * 세션을 끊지 않고 한 번 갱신해 시각을 다시 심는다.
     */
    if (loginTime === null) return reissueUserToken();

    const elapsed = Date.now() - loginTime;

    // 로그인 후 30일 경과 시 자동 로그아웃 (기존 동작)
    if (elapsed >= USER_MAX_SESSION_MS) {
        console.log('[UserAuth] 로그인 후 30일이 지나 로그아웃합니다.');
        await logoutExpiredSession(token);
        return false;
    }

    if (force || elapsed >= USER_REISSUE_AFTER_MS) {
        return reissueUserToken();
    }
    return true;   // 아직 유효 — 아무것도 하지 않는다
};

/*
 * ════════════════════════════════════════════════════════════════════════════════
 *  401 자동갱신 등록
 * ════════════════════════════════════════════════════════════════════════════════
 *  액세스토큰이 만료된 상태로 API 를 부르면 서버가 401 을 돌려준다.
 *  그때 axiosInstance 가 여기 등록된 함수로 토큰을 갱신하고 원래 요청을 다시 보낸다.
 *  타이머가 놓친 구간(탭 절전 / 기기 종료 / 시계 변경)을 메워서,
 *  리프레시토큰이 살아있는 동안에는 사용자가 만료를 체감하지 못하게 한다.
 *
 *  갱신 자체는 inFlight + 탭간 잠금으로 묶여 있으므로, 401 이 동시에 여러 개
 *  떠도 재발급 요청은 한 번만 나간다.
 * ════════════════════════════════════════════════════════════════════════════════
 */
// axiosInstance 를 모킹한 테스트처럼 이 함수가 없는 환경에서도 모듈이 깨지지 않게 방어한다
if (typeof registerTokenRefresher === 'function') {
    registerTokenRefresher('user', ensureUserToken, getUserToken);
}
